/*
 * Shared framer-motion variants + transitions for the Locobuzz site.
 *
 * Why this file exists: animation values were duplicated by hand across ~40
 * components (every industry hero, ai-stack page, landing sections). These are
 * the canonical values already in use — import them so new sections match the
 * approved design language (brand-blue, spring physics, scroll-jack takeovers)
 * instead of re-deriving stiffness/damping each time.
 *
 * Usage (in a 'use client' component):
 *   import { motion } from 'framer-motion';
 *   import { fadeUp, staggerContainer, viewportOnce } from '@/lib/motion';
 *
 *   <motion.ul variants={staggerContainer()} initial="hidden"
 *              whileInView="show" viewport={viewportOnce}>
 *     {items.map(it => <motion.li key={it.id} variants={fadeUp}>{it.label}</motion.li>)}
 *   </motion.ul>
 *
 * Reduced motion: prefer wrapping the tree once in
 *   <MotionConfig reducedMotion="user"> in app/layout.js, or call
 *   useReducedMotion() in a component and skip transforms.
 */

// ── Transitions ─────────────────────────────────────────────────────────────
// Entrance spring — the canonical headline/section reveal (industry heroes).
export const spring = { type: 'spring', damping: 16, stiffness: 100, mass: 1 };

// Slightly snappier spring for cards / secondary elements.
export const springSoft = { type: 'spring', damping: 18, stiffness: 110 };

// Non-spring ease for opacity-only fades where a spring would feel odd.
export const easeOut = { duration: 0.5, ease: [0.22, 1, 0.36, 1] };

// Default viewport config for whileInView reveals (animate once on enter).
export const viewportOnce = { once: true, amount: 0.2 };

// ── Variants (pair with initial="hidden" + animate/whileInView="show") ───────
export const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: spring },
};

export const fadeIn = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: easeOut },
};

export const slideInLeft = {
  hidden: { opacity: 0, x: -30 },
  show: { opacity: 1, x: 0, transition: springSoft },
};

export const slideInRight = {
  hidden: { opacity: 0, x: 30 },
  show: { opacity: 1, x: 0, transition: springSoft },
};

export const scaleIn = {
  hidden: { opacity: 0, scale: 0.94 },
  show: { opacity: 1, scale: 1, transition: springSoft },
};

// Container that reveals its children in sequence. Children need their own
// variant (e.g. fadeUp). Tune stagger/delay per section.
export const staggerContainer = (staggerChildren = 0.08, delayChildren = 0) => ({
  hidden: {},
  show: { transition: { staggerChildren, delayChildren } },
});

// ── Scroll-jack ──────────────────────────────────────────────────────────────
// Shared useScroll offset for sticky horizontal / takeover sections. The full
// recipe (sticky frame scale + horizontal track translate) lives in
// components/landing/Outcomes.jsx. Word-by-word text reveal already exists as
// components/primitives/ScrollRevealText.jsx — reuse it rather than rebuilding.
export const scrollJackOffset = ['start start', 'end end'];
